// @ts-nocheck
import { Component, Suspense } from 'react'
import { colors } from '@lmc/common/constants'
import Field from '../../../fields'
import { ErrorBox, HelpLink } from '../../../generic'
import { EmarStockFieldsSection } from '../components'
import { getEmarConfirmProps } from '../components/getEmarConfirmProps'
import {
  EmarBlisterPackField,
  EmarControlledDrugField,
  EmarHomelyRemedyField,
  EmarOverTheCounterField,
  EmarRouteField,
  EmarSecondSignatureField,
} from '../fields'
import { Text } from '@lmc/ui-kit'
import { ButtonOLD } from '@lmc/ui-kit'

const PrepopNote = () => (
  <div style={styles.prepopNote}>
    Information in blue has been taken from the NHS dm+d database
  </div>
)

/**
@description Renders the fourth step in the Add eMar Medication modal

@param {array} errors. All form field errors. If a field is incomplete, a
string will be added to this array.

@param {object} fields. The form fields.

@param {string} formId. addEmarMedication. Identifies the form.

@param {object} formState. The form state. Stores the medication name.

@param {function} goToNextStep. Fires when the Next Step button is pressed. 
Takes the user to the next modal step

@param {object} resident. The resident.

@param {function} setFormField. Sets the form fields. 

@param {function} validateFormFields. Validates the form fields and makes
sure that all required fields have been completed.

@returns {function} AddMedDetailsStep.
*/
export default class AddMedDetailsStep extends Component {
  stepRef = null

  setStepRef = (node) => (this.stepRef = node)

  componentDidMount() {
    const { stepRef } = this
    const {
      mixpanel,
      fields: { quantity_comment },
    } = this.props

    if (stepRef) {
      stepRef?.scrollIntoView()
    }

    if (quantity_comment) {
      this.setState({ isShowingQuantityComment: true })
    }
    if (mixpanel) mixpanel.track('CO_emar_modal_selected_med')
  }

  state = {
    isShowingQuantityComment: false,
  }

  onNext = () => {
    const { goToNextStep } = this.props
    goToNextStep()
  }

  handleNext = () => {
    const { stepRef } = this
    const { validateFormFields } = this.props

    // this.onNext()

    validateFormFields({
      onComplete: () => this.onNext(),
      onError: () => {
        if (stepRef) {
          stepRef?.scrollIntoView({ behavior: 'smooth' })
        }
      },
    })
  }

  // Comment functions

  toggleComment = () => {
    this.setState((prevState) => ({
      isShowingQuantityComment: !prevState.isShowingQuantityComment,
    }))
  }

  clearComment = (commentKey) => {
    const { setFormField } = this.props

    this.toggleComment()
    setFormField({ key: commentKey, value: null })
  }

  // Generic field render functions

  renderFieldTitle = (props) => {
    const { title, hasError, note, renderRight } = props

    let fieldTitleStyle = styles.fieldTitle

    if (hasError) {
      fieldTitleStyle = { ...fieldTitleStyle, color: colors.red }
    }

    return (
      <div style={styles.fieldTitleContainer}>
        <div>
          <Text style={fieldTitleStyle} as="h5">
            <div>
              {title} <span style={{ color: colors.pink }}>*</span>
            </div>
          </Text>
          <span style={{ fontSize: 12, color: colors.darkgrey }}>
            {note || null}
          </span>
        </div>
        {renderRight && renderRight()}
      </div>
    )
  }

  renderQuantityComment = (commentKey) => {
    const { fields, formId } = this.props
    const { isShowingQuantityComment } = this.state

    if (!isShowingQuantityComment) {
      return (
        <div style={styles.commentContainer}>
          <a className="add-comment" onClick={() => this.toggleComment()}>
            Add comment
          </a>
        </div>
      )
    }

    return (
      <div style={styles.commentContainer}>
        <Suspense fallback={<div>Loading...</div>}>
          <Field
            className="lmc-sub-card-edit"
            formId={formId}
            keys={commentKey}
            layout="no-label"
            maxLength={2000}
            style={styles.comment}
            type="textarea"
            values={fields}
          />
        </Suspense>
        <a
          className="cancel-add-comment"
          onClick={() => this.clearComment(commentKey)}
        >
          Cancel
        </a>
      </div>
    )
  }

  // Render field sections

  renderRouteField = () => {
    const {
      fields,
      formId,
      formState: { routePrePopped },
      setFormState,
    } = this.props

    return (
      <EmarRouteField
        fields={fields}
        formId={formId}
        onChange={() => {
          setFormState({ key: 'routePrePopped', value: false })
        }}
        requireConfirm={!!routePrePopped}
      />
    )
  }

  renderSecondSignatureField = () => {
    const { editState, fields, formId, formState, setFormState } = this.props

    const fieldKey = 'secondSignatureRequired'

    return (
      <EmarSecondSignatureField
        confirmProps={getEmarConfirmProps()}
        editState={editState}
        fields={fields}
        formId={formId}
        formState={formState}
        onConfirm={() => {
          setFormState({ key: `${fieldKey}PrePopped`, value: false })
        }}
        requireConfirm={!!formState[`${fieldKey}PrePopped`]}
      />
    )
  }

  renderControlledDrugField = () => {
    const { editState, fields, formId, formState, setFormState } = this.props

    const fieldKey = 'controlledDrug'

    return (
      <EmarControlledDrugField
        confirmProps={getEmarConfirmProps()}
        editState={editState}
        fields={fields}
        formId={formId}
        formState={formState}
        onConfirm={() => {
          setFormState({ key: `${fieldKey}PrePopped`, value: false })
        }}
        requireConfirm={!!formState[`${fieldKey}PrePopped`]}
      />
    )
  }

  renderHomelyRemedyField = () => {
    const { fields, formId, formState, setFormState } = this.props
    const fieldKey = 'homeRemedy'

    return (
      <EmarHomelyRemedyField
        confirmProps={getEmarConfirmProps()}
        fields={fields}
        formId={formId}
        onConfirm={() => {
          setFormState({ key: `${fieldKey}PrePopped`, value: false })
        }}
        requireConfirm={!!formState[`${fieldKey}PrePopped`]}
      />
    )
  }

  renderOverTheCounterField = () => {
    const { fields, formId, formState, setFormState } = this.props
    const fieldKey = 'overTheCounter'

    return (
      <EmarOverTheCounterField
        confirmProps={getEmarConfirmProps()}
        fields={fields}
        formId={formId}
        onConfirm={() => {
          setFormState({ key: `${fieldKey}PrePopped`, value: false })
        }}
        requireConfirm={!!formState[`${fieldKey}PrePopped`]}
      />
    )
  }

  renderBlisterPackField = () => {
    const { errors, fields, formId, formState, setFormState } = this.props
    const fieldKey = 'blisterPack'

    return (
      <EmarBlisterPackField
        confirmProps={getEmarConfirmProps()}
        errors={errors}
        fields={fields}
        formId={formId}
        onConfirm={() => {
          setFormState({ key: `${fieldKey}PrePopped`, value: false })
        }}
        renderFieldTitle={this.renderFieldTitle}
        requireConfirm={!!formState[`${fieldKey}PrePopped`]}
      />
    )
  }

  renderDescriptionField = () => {
    const { fields, formId } = this.props

    return (
      <div style={styles.fieldSubContainer}>
        <Suspense fallback={<div>Loading...</div>}>
          <Field
            format
            formId={formId}
            keys="description"
            label="Description (shape, colour) of medication"
            labelNote="This will be shown to carers whilst they are administering medication."
            labelStyle={{
              ...styles.routeLabel,
              ...styles.descriptionLabel,
            }}
            layout="column"
            placeholder="For example, white oval"
            type="text"
            values={fields}
          />
        </Suspense>
      </div>
    )
  }

  renderAdditionalSupportField = () => {
    const { fields, formId, resident } = this.props

    const title = `What additional support does ${
      resident?.preferredName || resident.name.first
    } need with this medication?`

    return (
      <div style={styles.userGeneratedFieldContainer}>
        <Suspense fallback={<div>Loading...</div>}>
          <Field
            compact
            displayLabel
            fieldType="textarea"
            formId={formId}
            keys="additionalSupport"
            layout="no-label"
            label={title}
            style={{ resize: 'none' }}
            type="userGeneratedFieldLayout"
            values={fields}
          />
        </Suspense>
      </div>
    )
  }

  // Render section functions

  renderErrorBox = () => {
    const { errors } = this.props

    return <ErrorBox errors={errors} style={styles.errorBox} />
  }

  renderFields = () => (
    <div style={styles.fieldsContainer}>
      <PrepopNote />
      {this.renderRouteField()}
      {this.renderSecondSignatureField()}
      {this.renderControlledDrugField()}
      {this.renderHomelyRemedyField()}
      {this.renderOverTheCounterField()}
      {this.renderBlisterPackField()}
      {this.renderDescriptionField()}
      {this.renderAdditionalSupportField()}
    </div>
  )

  renderFooter = () => (
    <div style={styles.footer}>
      <HelpLink supportLink="https://support.logmycare.co.uk/en/articles/4851795-how-do-i-add-a-medication" />
      <ButtonOLD onClick={() => this.handleNext()} type="primary">
        Next Step
      </ButtonOLD>
    </div>
  )

  // Main render function

  render() {
    const { editState, eMARStockEnabled } = this.props
    // Don't render wrapping styles for the Edit form
    return (
      <div style={editState ? {} : styles.container}>
        {!editState && <div ref={this.setStepRef} style={styles.topSpacing} />}
        {this.renderErrorBox()}
        {this.renderFields()}
        {eMARStockEnabled && <EmarStockFieldsSection {...this.props} />}
        {!editState && this.renderFooter()}
      </div>
    )
  }
}

const styles = {
  comment: {
    marginBottom: 16,
  },
  commentContainer: {
    maxWidth: '100%',
    marginTop: 16,
  },
  container: {
    alignItems: 'flex-start',
    display: 'flex',
    flexDirection: 'column',
    margin: 'auto',
    maxWidth: 960,
    padding: '0px 10px',
  },
  descriptionLabel: {
    display: 'flex',
    flexFlow: 'column',
  },
  errorBox: {
    margin: '10px 0px 8px 0px',
  },
  fieldTitle: {
    display: 'flex',
    justifyContent: 'space-between',
    lineHeight: '22px',
    // marginBottom: 16
  },
  fieldTitleContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: 16,
  },
  fieldsContainer: {
    // marginTop: 18,
    width: '100%',
  },
  fieldSubContainer: {
    background: colors.white,
    borderRadius: 15,
    marginBottom: 16,
    padding: 16,
  },
  footer: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: 20,
    width: '100%',
  },
  header: {
    lineHeight: '36px',
    alignSelf: 'center',
    marginBottom: 31,
    marginTop: 20,
  },
  medicationTitleContainer: {
    marginTop: 25,
    width: '100%',
  },
  prepopNote: {
    color: colors.darkgrey,
    fontSize: 12,
    lineHeight: '18px',
    marginBottom: 16,
  },
  quantityFieldEntry: {
    margin: '0 12px 12px 0',
  },
  quantityFieldsContainer: {
    display: 'flex',
    flexFlow: 'row wrap',
    marginBottom: '-12px',
  },
  routeLabel: {
    fontWeight: 700,
    fontSize: 16,
    lineHeight: '22px',
    marginBottom: 16,
  },
  text: {
    lineHeight: '22px',
  },
  topSpacing: {
    height: 225,
  },
  userGeneratedFieldContainer: {
    marginBottom: 16,
  },
}
